import { useAppContext } from "@/app/providers/AppContext";
import WatchDetailPanel from "@/domains/watches/components/WatchDetailPanel";
import WatchMonitoringSummary from "@/domains/watches/components/WatchMonitoringSummary";
import WatchSummary from "@/domains/watches/components/WatchSummary";
import WatchTickerBadge from "@/domains/watches/components/WatchTickerBadge/WatchTickerBadge";
import { getDefaultsByIntent } from "@/domains/watches/hooks/useWatchDefaults";
import { useWatchHistory } from "@/domains/watches/hooks/useWatchHistory";
import { useWatchListState } from "@/domains/watches/hooks/useWatchListState";
import { useWatchRecipeState } from "@/domains/watches/hooks/useWatchRecipeState";
import {
    getWatchContractDisplay,
    getWatchContractShortLabel,
    getWatchContractTitle,
    getWatchExpirationLabel,
    getWatchExpirationPrefix,
    isStructureEntryWatch,
    isWatchExpired,
} from "@/domains/watches/lib/watchFormatting";
import { getContractExpiryInfo, getMonitoringWindowState } from "@/domains/watches/lib/watchMonitoringWindow";
import { parseOptionContractDetails } from "@/domains/watches/lib/watchOptionContracts";
import { buildUpdateWatchBody } from "@/domains/watches/lib/watchPayloads";
import {
    makeMultiLegDraft,
    makeMultiLegDraftFromPosition,
    type MultiLegDraft,
} from "@/domains/watches/lib/multiLegPosition";
import type {
    AlertMode,
    ContractMode,
    ContractType,
    IntentConfig,
    TargetDirection,
    VolatilitySensitivity,
    WatchContractsPayload,
    WatchIntentDefinition,
    RecipePreview,
} from "@/domains/watches/lib/watchTypes";
import { FALLBACK_INTENT_DEFINITIONS } from "@/domains/watches/lib/watchTypes";
import { WatchTableSkeleton } from "@/shared/components/PortalPageSkeletons";
import type { WatchCondition, WatchRule } from "@/shared/lib/types";
import { Card, Mono, Pill, StatDot, T } from "@/shared/ui/DesignLib";
import { Button } from "@/shared/ui/button";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/shared/ui/dialog";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/shared/ui/select";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/shared/ui/table";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/shared/ui/tooltip";
import {
    Ban,
    ChevronDown,
    ChevronUp,
    Edit2,
    Pause,
    Play,
    Plus,
    RefreshCw,
    Search,
    Trash2,
    XCircle,
} from "lucide-react";
import { Fragment, type ReactElement, useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

interface WatchConfigResponse {
  supported_intents?: IntentConfig[];
}

interface WatchUpdatePreview {
  confirmationToken: string;
  payloadHash: string;
  changedFields: string[];
}

const getErrorMessage = (error: unknown, fallback: string) =>
  error instanceof Error && error.message ? error.message : fallback;

const getNumberParam = (
  params: Record<string, unknown>,
  key: string,
  fallback: number,
) => typeof params[key] === "number" ? params[key] : fallback;

const getTargetDirectionParam = (
  params: Record<string, unknown>,
  fallback: TargetDirection,
): TargetDirection =>
  params.direction === "above" || params.direction === "below"
    ? params.direction
    : fallback;

const getVolatilitySensitivityParam = (
  params: Record<string, unknown>,
  fallback: VolatilitySensitivity,
): VolatilitySensitivity =>
  params.sensitivity === "low" ||
  params.sensitivity === "medium" ||
  params.sensitivity === "high"
    ? params.sensitivity
    : fallback;

const getRecipePreview = (value: unknown): RecipePreview | null =>
  value !== null && typeof value === "object"
    ? (value as RecipePreview)
    : null;

const withPositionPnlCondition = (
  current: WatchCondition[],
  operator: "gte" | "lte",
  amount: number | null,
) => {
  const preserved = current.filter((condition) => !(
    condition.dimension === "position_unrealized_pnl_usd"
    && condition.operator === operator
  ));
  if (amount === null || !Number.isFinite(amount)) return preserved;
  return [
    ...preserved,
    {
      dimension: "position_unrealized_pnl_usd",
      operator,
      threshold: operator === "lte" ? -Math.abs(amount) : Math.abs(amount),
    },
  ];
};

function InfoTip({ text }: { text: string }) {
  const [show, setShow] = useState(false);
  return (
    <span
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        marginLeft: 4,
      }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 14,
          height: 14,
          borderRadius: "50%",
          border: `1px solid ${T.borderSoft}`,
          fontSize: "0.7rem",
          color: T.dim,
          cursor: "help",
          transition: "all 0.15s",
          ...(show ? { borderColor: T.primary, color: T.primary } : {}),
        }}
      >
        i
      </span>
      {show && (
        <span
          style={{
            position: "absolute",
            bottom: "calc(100% + 6px)",
            left: "50%",
            transform: "translateX(-50%)",
            background: T.card2,
            color: T.text,
            fontFamily: T.mono,
            fontSize: "0.68rem",
            lineHeight: 1.45,
            padding: "6px 10px",
            borderRadius: 4,
            whiteSpace: "nowrap",
            maxWidth: 220,
            textAlign: "center",
            zIndex: 100,
            boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
            pointerEvents: "none",
            border: `1px solid ${T.borderSoft}`,
          }}
        >
          {text}
        </span>
      )}
    </span>
  );
}

const WatchesTab = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const tickerPageSource = `${location.pathname}${location.search}`;
  const {
    watches,
    loadWatches,
    watchesLoading,
    api,
  } = useAppContext();
  const [watchContracts, setWatchContracts] =
    useState<WatchContractsPayload | null>(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editingWatchId, setEditingWatchId] = useState<string | null>(null);
  const [updatePreview, setUpdatePreview] = useState<WatchUpdatePreview | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Confirmation States
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<{
    id: string;
    type: string;
  } | null>(null);

  // Expanded Edit States
  const [confidenceThreshold, setConfidenceThreshold] = useState(0.7);
  const [conditions, setConditions] = useState<WatchCondition[]>([]);
  const [conditionLogic, setConditionLogic] = useState("AND");
  const [editingWatch, setEditingWatch] = useState<WatchRule | null>(null);

  // Form State
  const [ticker, setTicker] = useState("");
  const [intent, setIntent] = useState("good_time_to_buy");
  const [contractMode, setContractMode] = useState<ContractMode>(
    "builder",
  );
  const [contractType, setContractType] = useState<ContractType>("call");
  const [contractExpiry, setContractExpiry] = useState("");
  const [contractStrike, setContractStrike] = useState("");
  const [manualOCC, setManualOCC] = useState("");
  const [positionMode, setPositionMode] = useState<"single" | "multi_leg">("single");
  const [multiLegDraft, setMultiLegDraft] = useState<MultiLegDraft>(() => makeMultiLegDraft());
  const [positionProfitTarget, setPositionProfitTarget] = useState<number | null>(null);
  const [positionLossLimit, setPositionLossLimit] = useState<number | null>(null);
  const [duration, setDuration] = useState(5);
  const [interval, setInterval] = useState(15);
  const [maxAlerts, setMaxAlerts] = useState(3);
  const [cooldown, setCooldown] = useState(120);
  const [marketHoursOnly, setMarketHoursOnly] = useState(true);
  const [ivThreshold, setIvThreshold] = useState(80);
  const [targetPrice, setTargetPrice] = useState<number | null>(null);
  const [targetDirection, setTargetDirection] = useState<TargetDirection>(
    "above",
  );
  const [volatilitySensitivity, setVolatilitySensitivity] =
    useState<VolatilitySensitivity>("medium");
  const [alertMode, setAlertMode] = useState<AlertMode>(
    "one_and_done",
  );
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [supportedIntents, setSupportedIntents] = useState<IntentConfig[]>([]);

  const {
    recipeConditions,
    syncRecipeState,
    recipeGroupViews,
    hasValidRecipeSelection,
    toggleRecipeCondition,
  } = useWatchRecipeState();

  const {
    watchSearch,
    setWatchSearch,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    totalPages,
    filteredWatches,
    paginatedWatches,
    activeWatchCount,
  } = useWatchListState({
    watches,
    loading: watchesLoading,
  });
  // Preserve legacy bookmarks while ensuring every new authoring session uses
  // the dedicated full-page route rather than a modal layered over this table.
  useEffect(() => {
    if (searchParams.get("create") === "true") {
      navigate("/portal/watches/new", { replace: true });
    }
  }, [navigate, searchParams]);
  const intentDefinitions = (() => {
    const backendDefinitions = watchContracts?.intent_definitions || [];
    const merged = new Map<string, WatchIntentDefinition>();

    for (const definition of FALLBACK_INTENT_DEFINITIONS) {
      merged.set(definition.intent, definition);
    }
    for (const definition of backendDefinitions) {
      merged.set(definition.intent, {
        ...(merged.get(definition.intent) || {}),
        ...definition,
      });
    }

    return Array.from(merged.values()).filter(
      (definition) =>
        definition.intent !== "iv_rank_threshold" &&
        definition.intent !== "volatility_spike",
    );
  })();
  const intentDefinitionMap = intentDefinitions.reduce(
    (acc, definition) => {
      acc[definition.intent] = definition;
      return acc;
    },
    {} as Record<string, WatchIntentDefinition>,
  );

  const handleIntentChange = useCallback((newIntent: string) => {
    setIntent(newIntent);
    const defaults = getDefaultsByIntent(newIntent);
    const intentParams = defaults.intentParams;
    syncRecipeState(null);
    setMarketHoursOnly(defaults.policy.market_hours_only);
    setConfidenceThreshold(getNumberParam(intentParams, "confidence_threshold", 0.7));
    setVolatilitySensitivity(getVolatilitySensitivityParam(intentParams, "medium"));
    setIvThreshold(getNumberParam(intentParams, "threshold", 80));
    setTargetPrice(
      typeof intentParams.target_price === "number"
        ? intentParams.target_price
        : null,
    );
    setTargetDirection(getTargetDirectionParam(intentParams, "above"));
    setDuration(defaults.policy.max_duration_days);
    setInterval(defaults.policy.evaluation_interval_minutes);
    setAlertMode(defaults.alertMode);
    setMaxAlerts(defaults.policy.max_alerts_per_watch);
    setCooldown(defaults.policy.cooldown_minutes);
    const nextIntentRequiresContract =
      supportedIntents.find((item) => item.intent === newIntent)?.requires_option_contract ??
      ["good_time_to_buy", "good_time_to_sell", "iv_rank_threshold", "volatility_spike"].includes(newIntent);
    if (!nextIntentRequiresContract) {
      setPositionMode("single");
      setMultiLegDraft(makeMultiLegDraft());
      setPositionProfitTarget(null);
      setPositionLossLimit(null);
      syncRecipeState(null);
    }
  }, [supportedIntents, syncRecipeState]);

  // Removed redundant loadWatches on mount, relying on AppContext state

  useEffect(() => {
    const loadWatchContracts = async () => {
      try {
        const data = await api("GET", "/api/watches/contracts", undefined, {
          silent: true,
        });
        if (data) {
          setWatchContracts(data);
        }
      } catch {
        // Non-baseline wizard goals remain unavailable until authenticated
        // server metadata arrives; never substitute local rollout defaults.
      }
    };
    loadWatchContracts();
  }, [api]);

  useEffect(() => {
    if (intentDefinitions.length === 0) return;
    if (intentDefinitionMap[intent]) return;
    const fallbackTimer = window.setTimeout(
      () => handleIntentChange(intentDefinitions[0].intent),
      0,
    );
    return () => window.clearTimeout(fallbackTimer);
  }, [handleIntentChange, intent, intentDefinitionMap, intentDefinitions]);

  // Auto-expand and load history for a specific watch from query param
  useEffect(() => {
    const loadWatchConfig = async () => {
      try {
        const data = await api<WatchConfigResponse>("GET", "/api/watches/config");
        const intents = Array.isArray(data?.supported_intents)
          ? data.supported_intents
          : [];
        setSupportedIntents(intents);
      } catch {
        // The creation form remains usable with bundled intent definitions.
      }
    };
    loadWatchConfig();
  }, [api]);

  useEffect(() => {
    if (
      supportedIntents.length === 0 ||
      supportedIntents.some((item) => item.intent === intent)
    ) return;
    const fallbackTimer = window.setTimeout(
      () => handleIntentChange(supportedIntents[0].intent),
      0,
    );
    return () => window.clearTimeout(fallbackTimer);
  }, [handleIntentChange, intent, supportedIntents]);

  const currentIntentConfig =
    supportedIntents.find((item) => item.intent === intent) || null;
  const intentRequiresContract =
    currentIntentConfig?.requires_option_contract ??
    [
      "good_time_to_buy",
      "good_time_to_sell",
      "iv_rank_threshold",
      "volatility_spike",
    ].includes(intent);
  const isPositionWatch = positionMode === "multi_leg";

  const {
    expandedWatches,
    detailData,
    detailLoading,
    detailErrors,
    toggleExpand: toggleExpandHistory,
    refreshLogs: refreshWatchLogs,
    invalidateWatchDetail,
  } = useWatchHistory({
    api,
    watches,
    searchParams,
    setSearchParams,
    currentPage,
    setCurrentPage,
    itemsPerPage,
  });

  const toggleExpand = async (watchId: string) => {
    try {
      await toggleExpandHistory(watchId);
    } catch {
      toast.error("Failed to load history");
    }
  };

  const refreshLogs = async (watchId: string) => {
    try {
      await refreshWatchLogs(watchId);
      toast.success("Logs refreshed");
    } catch {
      toast.error("Failed to refresh logs");
    }
  };

  const getWatchStatusColor = (status?: string) => {
    const normalized = status?.toLowerCase();
    if (normalized === "active") return T.green;
    if (normalized === "paused") return "#f59e0b";
    if (normalized === "cancelled") return T.dim;
    if (normalized === "expired") return "#ef4444";
    if (normalized === "completed") return "#06b6d4";
    return T.amber;
  };

  const renderWatchStatus = (w: WatchRule) => (
    <Pill
      active={w.status?.toLowerCase() === "active"}
      color={getWatchStatusColor(w.status)}
      style={w.status?.toLowerCase() === "expired" ? { opacity: 0.6 } : undefined}
    >
      {w.status?.toLowerCase() === "active" && <StatDot active />}
      {w.status?.toUpperCase()}
    </Pill>
  );

  const navigateToWatchTicker = (w: WatchRule) => {
    if (!w.ticker) return;
    navigate(`/ticker/${w.ticker}`, {
      state: { from: tickerPageSource },
    });
  };

  const beginPositionCopy = (watchId: string, mode: "duplicate" | "replace") => {
    navigate(`/portal/watches/new?sourceWatchId=${encodeURIComponent(watchId)}&mode=${mode}`);
  };

  const renderActionTooltip = (label: string, child: ReactElement) => (
    <Tooltip>
      <TooltipTrigger asChild>{child}</TooltipTrigger>
      <TooltipContent
        side="top"
        align="center"
        sideOffset={8}
        showArrow={false}
        className="text-center"
      >
        {label}
      </TooltipContent>
    </Tooltip>
  );

  const renderWatchActions = (w: WatchRule) => (
    <TooltipProvider>
      <div className="watches-actions-cell flex items-center justify-end">
        {renderActionTooltip(
          expandedWatches[w.watch_id]
            ? "Hide watch details"
            : "Show watch details",
          <button
            className="btn-ghost"
            aria-label={
              expandedWatches[w.watch_id]
                ? "Hide watch details"
                : "Show watch details"
            }
            aria-expanded={Boolean(expandedWatches[w.watch_id])}
            aria-controls={`watch-detail-${w.watch_id}`}
            onClick={(e) => {
              e.stopPropagation();
              toggleExpand(w.watch_id);
            }}
          >
            {expandedWatches[w.watch_id] ? (
              <ChevronUp size={15} strokeWidth={1.8} />
            ) : (
              <ChevronDown size={15} strokeWidth={1.8} />
            )}
            <span>Details</span>
          </button>,
        )}
        {!isStructureEntryWatch(w) && !isWatchExpired(w) &&
          w.status?.toLowerCase() !== "completed" &&
          renderActionTooltip(
            "Edit",
            <button
              className="btn-ghost"
              aria-label="Edit"
              onClick={(e) => {
                e.stopPropagation();
                openEditDialog(w);
              }}
            >
              <Edit2 size={15} strokeWidth={1.5} />
              <span>Edit</span>
            </button>,
          )}
        {w.status?.toLowerCase() === "active" &&
          renderActionTooltip(
            "Pause",
            <button
              className="btn-ghost"
              aria-label="Pause"
              onClick={(e) => {
                e.stopPropagation();
                handleAction(w.watch_id, "pause");
              }}
            >
              <Pause size={15} strokeWidth={1.5} />
              <span>Pause</span>
            </button>,
          )}
        {w.status?.toLowerCase() === "paused" &&
          renderActionTooltip(
            "Resume",
            <button
              className="btn-ghost"
              aria-label="Resume"
              onClick={(e) => {
                e.stopPropagation();
                handleAction(w.watch_id, "resume");
              }}
            >
              <Play size={15} strokeWidth={1.5} />
              <span>Resume</span>
            </button>,
          )}
        {w.status?.toLowerCase() !== "cancelled" &&
          w.status?.toLowerCase() !== "expired" &&
          w.status?.toLowerCase() !== "completed" &&
          renderActionTooltip(
            "Cancel",
            <button
              className="btn-ghost"
              aria-label="Cancel"
              onClick={(e) => {
                e.stopPropagation();
                handleAction(w.watch_id, "cancel");
              }}
            >
              <XCircle size={15} strokeWidth={1.5} />
              <span>Cancel</span>
            </button>,
          )}
        {renderActionTooltip(
          "Delete",
          <button
            className="btn-ghost"
            aria-label="Delete"
            style={{ color: T.red }}
            onClick={(e) => {
              e.stopPropagation();
              handleAction(w.watch_id, "delete");
            }}
          >
            <Trash2 size={15} strokeWidth={1.5} />
            <span>Delete</span>
          </button>,
        )}
      </div>
    </TooltipProvider>
  );

  const alertModeDefaultsForIntent = (
    watchIntent: string,
    mode: "one_and_done" | "keep_watching",
  ) => {
    if (mode === "one_and_done") {
      return { maxAlerts: 1, cooldown: 0 };
    }
    if (
      watchIntent === "iv_rank_threshold" ||
      watchIntent === "volatility_spike"
    ) {
      return { maxAlerts: 20, cooldown: 240 };
    }
    if (watchIntent === "price_target") {
      return { maxAlerts: 20, cooldown: 60 };
    }
    return { maxAlerts: 20, cooldown: 120 };
  };

  const applyAlertModeDefaults = (
    mode: "one_and_done" | "keep_watching",
    watchIntent: string = intent,
  ) => {
    setAlertMode(mode);
    const defaults = alertModeDefaultsForIntent(watchIntent, mode);
    setMaxAlerts(defaults.maxAlerts);
    setCooldown(defaults.cooldown);
  };

  const resetForm = () => {
    setTicker("");
    setIntent("good_time_to_buy");
    setContractMode("builder");
    setContractType("call");
    setContractExpiry("");
    setContractStrike("");
    setManualOCC("");
    setPositionMode("single");
    setMultiLegDraft(makeMultiLegDraft());
    setPositionProfitTarget(null);
    setPositionLossLimit(null);
    setConfidenceThreshold(0.7);
    setConditions([]);
    setConditionLogic("AND");
    setDuration(5);
    setInterval(15);
    setMaxAlerts(3);
    setCooldown(120);
    setMarketHoursOnly(true);
    setIvThreshold(80);
    setTargetPrice(null);
    setTargetDirection("above");
    setVolatilitySensitivity("medium");
    setAlertMode("one_and_done");
    setShowAdvanced(false);
    syncRecipeState(null);
  };

  const handleUpdateWatch = async () => {
    if (!editingWatchId) return;

    try {
      const hasVersionedPositionTriggers = editingWatch?.evaluation_spec?.subject === "POSITION_EXIT";
      const invalidProfit = positionProfitTarget !== null
        && (!Number.isFinite(positionProfitTarget) || positionProfitTarget <= 0);
      const invalidLoss = positionLossLimit !== null
        && (!Number.isFinite(positionLossLimit) || positionLossLimit <= 0);
      if (editingWatch?.position && !hasVersionedPositionTriggers && (
        (positionProfitTarget === null && positionLossLimit === null)
        || invalidProfit
        || invalidLoss
      )) {
        toast.error("Enter a positive profit target, loss limit, or both");
        return;
      }
      if (intent === "price_target" && !targetPrice) {
        toast.error("Target price is required");
        return;
      }
      setIsSubmitting(true);
      const watchIntent = editingWatch?.intent || "good_time_to_buy";
      const body = buildUpdateWatchBody({
        watchIntent,
        duration: getEffectiveDuration(),
        interval,
        maxAlerts,
        cooldown,
        marketHoursOnly,
        alertMode,
        confidenceThreshold,
        ivThreshold,
        targetPrice,
        targetDirection,
        volatilitySensitivity,
        conditions,
        conditionLogic,
        recipeConditions,
      });

      if (!updatePreview) {
        const preview = await api<{
          confirmation_token?: string;
          payload_hash?: string;
          changed_fields?: string[];
        }>("POST", `/api/watches/${editingWatchId}/update/preview`, body);
        if (
          typeof preview.confirmation_token !== "string" ||
          typeof preview.payload_hash !== "string"
        ) {
          throw new Error("The server could not secure this Watch edit. Try again.");
        }
        setUpdatePreview({
          confirmationToken: preview.confirmation_token,
          payloadHash: preview.payload_hash,
          changedFields: Array.isArray(preview.changed_fields)
            ? preview.changed_fields
            : [],
        });
        return;
      }

      await api("POST", `/api/watches/${editingWatchId}/update/confirm`, {
        confirmation_token: updatePreview.confirmationToken,
        payload_hash: updatePreview.payloadHash,
      });
      await loadWatches();
      await invalidateWatchDetail(editingWatchId, true);
      toast.success("Watch updated");
      setIsEditOpen(false);
      setEditingWatchId(null);
      setEditingWatch(null);
      setUpdatePreview(null);
      resetForm();
    } catch (e: unknown) {
      if (updatePreview) setUpdatePreview(null);
      toast.error(getErrorMessage(e, "Failed to update watch"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const openEditDialog = (w: WatchRule) => {
    if (isStructureEntryWatch(w)) {
      toast.message("Setup details are fixed after creation. Create a new Watch to monitor a different setup.");
      return;
    }
    if ((w?.status || "").toLowerCase() === "expired") return;
    if (
      typeof w?.expires_at === "number" &&
      w.expires_at > 0 &&
      w.expires_at * 1000 <= Date.now()
    )
      return;
    setEditingWatchId(w.watch_id);
    setEditingWatch(w);
    setUpdatePreview(null);
    setPositionMode(w.position ? "multi_leg" : "single");
    setTicker(w.ticker);
    setIntent(w.intent);
    setDuration(w.policy?.max_duration_days || 5);
    setInterval(w.policy?.evaluation_interval_minutes || 15);
    const existingMaxAlerts = w.policy?.max_alerts_per_watch || 3;
    const existingCooldown = w.policy?.cooldown_minutes || 120;
    setAlertMode(existingMaxAlerts <= 1 ? "one_and_done" : "keep_watching");
    setMaxAlerts(existingMaxAlerts);
    setCooldown(existingCooldown);
    setMarketHoursOnly(w.policy?.market_hours_only ?? true);

    const intentParams = w.intent_params || {};
    setConfidenceThreshold(getNumberParam(intentParams, "confidence_threshold", 0.7));
    // Load intent-specific params
    if (
      w.intent === "iv_rank_threshold" &&
      typeof intentParams.threshold === "number"
    ) {
      setIvThreshold(intentParams.threshold);
    }
    if (w.intent === "price_target") {
      if (typeof intentParams.target_price === "number")
        setTargetPrice(intentParams.target_price);
      setTargetDirection(getTargetDirectionParam(intentParams, "above"));
    }
    if (w.intent === "volatility_spike") {
      setVolatilitySensitivity(getVolatilitySensitivityParam(intentParams, "medium"));
    }
    const parsedContract = parseOptionContractDetails(w.option_contract);
    setManualOCC(w.option_contract || "");
    setContractMode(parsedContract ? "builder" : "manual");
    setContractExpiry(parsedContract?.expiry || "");
    setContractStrike(parsedContract?.strike || "");
    setContractType(parsedContract?.type || "call");
    setConditions(w.conditions || []);
    setConditionLogic(String(w.condition_logic || "AND"));
    const positionProfitCondition = (w.conditions || []).find(
      (condition) => condition.dimension === "position_unrealized_pnl_usd" && condition.operator === "gte",
    );
    const positionLossCondition = (w.conditions || []).find(
      (condition) => condition.dimension === "position_unrealized_pnl_usd" && condition.operator === "lte",
    );
    setPositionProfitTarget(positionProfitCondition ? Math.abs(Number(positionProfitCondition.threshold)) : null);
    setPositionLossLimit(positionLossCondition ? Math.abs(Number(positionLossCondition.threshold)) : null);
    if (w.position) {
      const positionDraft = makeMultiLegDraftFromPosition(w.position);
      if (positionDraft) setMultiLegDraft(positionDraft);
    }
    syncRecipeState(getRecipePreview(w.recipe));
    setShowAdvanced(false);

    setIsEditOpen(true);
  };

  const handleRefresh = async () => {
    try {
      setIsRefreshing(true);
      await loadWatches();
      toast.success("Watches refreshed");
    } catch {
      toast.error("Failed to refresh watches");
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleAction = async (
    id: string,
    action: string,
    skipConfirm = false,
  ) => {
    try {
      if (!skipConfirm) {
        if (action === "cancel" || action === "delete") {
          setPendingAction({ id, type: action });
          setIsConfirmOpen(true);
          return;
        }
      }

      setIsSubmitting(true);
      await api("POST", `/api/watches/${id}/${action}`);
      await loadWatches();
      await invalidateWatchDetail(id, action !== "delete");
      toast.success(`Watch ${action === "delete" ? "deleted" : action + "ed"}`);
    } catch {
      toast.error(`Unable to ${action} this watch. Its current status has not changed.`);
      await loadWatches();
    } finally {
      setIsSubmitting(false);
    }
  };

  const positionExpiryInfo = (() => {
    if (!isPositionWatch) return null;
    const dates = multiLegDraft.legs
      .map((leg) => parseOptionContractDetails(leg.optionContract)?.expiry)
      .filter((date) => Boolean(date));
    if (dates.length !== multiLegDraft.legs.length || dates.length === 0) return null;
    const earliest = dates.sort()[0];
    return getContractExpiryInfo({ contractMode: "builder", contractExpiry: earliest, manualOCC: "" });
  })();
  const contractExpiryInfo = isPositionWatch
    ? positionExpiryInfo
    : intentRequiresContract
      ? getContractExpiryInfo({ contractMode, contractExpiry, manualOCC })
      : null;
  const lockToExpiryWindow = Boolean(
    contractExpiryInfo && contractExpiryInfo.dte < 5,
  );
  const effectiveDuration = lockToExpiryWindow
    ? contractExpiryInfo!.dte
    : duration;
  const {
    monitoringWindowOptions,
    isCustomWindowSelected,
    suggestedCustomWindowDays,
    monitoringWindowSummary,
    isMonitoringWindowActive,
  } = getMonitoringWindowState({
    duration: effectiveDuration,
    contractExpiryInfo,
  });
  function getEffectiveDuration() {
    const expiryInfo = isPositionWatch
      ? positionExpiryInfo
      : intentRequiresContract
        ? getContractExpiryInfo({ contractMode, contractExpiry, manualOCC })
        : null;
    return expiryInfo && expiryInfo.dte < 5 ? expiryInfo.dte : duration;
  }
  const isMonitoringWindowDisabled = (days: number) =>
    lockToExpiryWindow && days > (contractExpiryInfo?.dte || 0);

  return (
    <div
      style={{ display: "flex", flexDirection: "column", gap: 24 }}
      className="fade-up min-h-[88vh]"
    >
      {/* Header */}
      <div
        className="watches-header"
      >
        <div className="watches-header-copy">
          <h1
            className="watches-page-title"
            style={{
              fontFamily: T.serif,
              fontWeight: 700,
              color: T.text,
              margin: 0,
              lineHeight: 1.1,
            }}
          >
            Active Watches
          </h1>
          <Mono size=".8rem">
            Managing {activeWatchCount} active monitors.
          </Mono>
        </div>

        <div className="watches-header-actions">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  onClick={handleRefresh}
                  disabled={isRefreshing}
                  variant="outline"
                  size="sm"
                  aria-label="Refresh Watches"
                >
                  <RefreshCw
                    size={16}
                    className={isRefreshing ? "animate-spin" : ""}
                  />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Refresh</TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <Button
            size="sm"
            className="w-fit md:w-auto flex items-center gap-2"
            onClick={() => navigate("/portal/watches/new")}
            aria-label="New Watch"
          >
            <Plus className="h-4 w-4" />
            <span className="hidden md:block">New Watch</span>
          </Button>
        </div>
      </div>
      <div className="watches-filter-bar">
        <div className="watches-search-field">
          <Search />
          <Input
            placeholder="Search watches by ticker, status, intent, or contract"
            value={watchSearch}
            onChange={(event) => setWatchSearch(event.target.value)}
          />
        </div>
        <Mono
          size=".7rem"
          color={T.dim}
          style={{ flex: "0 0 auto", whiteSpace: "nowrap" }}
        >
          {filteredWatches.length} of {watches.length} watches
        </Mono>
      </div>

      <Card className="watches-table-shell">
        <div style={{ display: "flex", flexDirection: "column" }}>
          {watchesLoading ? (
            <WatchTableSkeleton />
          ) : watches.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 px-6">
              <div style={{ marginTop: 16 }}>
                <div
                  className="text-center"
                  style={{
                    fontFamily: T.serif,
                    fontSize: ".88rem",
                    fontWeight: 700,
                    color: T.text,
                    marginBottom: 6,
                  }}
                >
                  No watches yet
                </div>
                <Mono className="text-center" size=".58rem" color={T.mid}>
                  Create a watch to monitor market conditions and get alerts when
                  conditions change.
                </Mono>
              </div>
              <Button
                size="sm"
                className="mt-6"
                onClick={() => navigate("/portal/watches/new")}
              >
                <Plus className="h-4 w-4 mr-2" /> New Watch
              </Button>
            </div>
          ) : filteredWatches.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 px-6">
              <div
                className="text-center"
                style={{
                  fontFamily: T.serif,
                  fontSize: ".95rem",
                  fontWeight: 700,
                  color: T.text,
                  marginBottom: 6,
                }}
              >
                No matching watches
              </div>
              <Mono className="text-center" size=".65rem" color={T.mid}>
                Try another ticker, status, intent, or contract.
              </Mono>
              <Button
                variant="outline"
                size="sm"
                className="mt-6"
                onClick={() => setWatchSearch("")}
              >
                Clear Search
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-secondary/60 border-border/50 hover:bg-secondary/60">
                  <TableHead className="watches-th pl-6 w-[300px]">
                    Contract / Ticker
                  </TableHead>
                  <TableHead className="watches-th w-[240px]">
                    Status / Intent
                  </TableHead>
                  <TableHead className="watches-th w-[130px]">
                    Created
                  </TableHead>
                  <TableHead className="watches-th text-right w-[110px]">
                    Alerts
                  </TableHead>
                  <TableHead className="watches-th text-right pr-6 w-[210px]">
                    Actions
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedWatches.map((w, idx) => (
                  <Fragment key={w.watch_id}>
                    <TableRow
                      className={`s${(idx % 8) + 1} fade-up watches-row border-border/40 group cursor-pointer`}
                      data-expanded={Boolean(expandedWatches[w.watch_id])}
                      onClick={() => { void toggleExpand(w.watch_id); }}
                    >
                      <TableCell className="pl-6 py-4">
                        <div className="flex items-center gap-3 min-w-[260px]">
                          <WatchTickerBadge
                            ticker={w.ticker}
                            logoDataUri={w.logo_data_uri}
                            fontFamily={T.display}
                            onOpen={() => navigateToWatchTicker(w)}
                          />
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigateToWatchTicker(w);
                            }}
                            className="watches-contract-copy text-left hover:opacity-80 transition-opacity"
                          >
                            <span
                              style={{
                                fontFamily: T.mono,
                                fontWeight: 700,
                                fontSize: ".9rem",
                                color: T.text,
                              }}
                            >
                              {w.ticker}
                            </span>
                            <Mono
                              size=".65rem"
                              color={T.dim}
                              className="watches-contract-label"
                              title={getWatchContractTitle(w)}
                            >
                              {getWatchContractShortLabel(w)}
                            </Mono>
                            <Mono
                              size=".5rem"
                              color={T.dim}
                              className="watches-expiry-label"
                              title={getWatchContractTitle(w)}
                            >
                              {getWatchExpirationPrefix(w)} {getWatchExpirationLabel(w)}
                            </Mono>
                          </button>
                        </div>
                      </TableCell>
                      <TableCell>
                        {renderWatchStatus(w)}
                        <div className="watches-intent">
                          <Mono size=".6rem" color={T.dim}>
                            {w.position ? "position P&L" : w.intent?.replace(/_/g, " ")}
                          </Mono>
                        </div>
                        <WatchMonitoringSummary summary={w.monitoring_summary} />
                      </TableCell>
                      <TableCell className="watches-date font-mono text-[11px] text-muted-foreground group-hover:text-foreground">
                        {w.created_at
                          ? new Date(w.created_at * 1000).toLocaleDateString()
                          : "-"}
                      </TableCell>
                      <TableCell className="text-right">
                        <Mono
                          size=".75rem"
                          color={T.text}
                          className="watches-alert-count"
                          data-empty={!w.alerts_sent}
                          style={{ fontWeight: 600 }}
                        >
                          {w.alerts_sent || 0}
                        </Mono>
                      </TableCell>
                      <TableCell className="pr-6">
                        {renderWatchActions(w)}
                      </TableCell>
                    </TableRow>

                    {expandedWatches[w.watch_id] && (
                      <TableRow className="border-border/40 bg-muted/10 hover:bg-muted/10">
                        <TableCell colSpan={5} className="p-0 whitespace-normal">
                          <div
                            id={`watch-detail-${w.watch_id}`}
                            role="region"
                            aria-label={`${w.ticker} watch details`}
                            style={{
                              padding: "18px 22px 22px 24px",
                              borderTop: `1px solid ${T.borderSoft}`,
                              boxShadow: "inset 2px 0 0 var(--primary)",
                              background: "color-mix(in srgb, var(--card) 60%, transparent)",
                            }}
                          >
                            <WatchDetailPanel
                              detail={detailData[w.watch_id]}
                              loading={Boolean(detailLoading[w.watch_id])}
                              error={detailErrors[w.watch_id]}
                              onRefresh={() => refreshLogs(w.watch_id)}
                              onEdit={() => openEditDialog(w)}
                              onPause={() => handleAction(w.watch_id, "pause")}
                              onResume={() => handleAction(w.watch_id, "resume")}
                              onCancel={() => handleAction(w.watch_id, "cancel")}
                              onDuplicate={() => beginPositionCopy(w.watch_id, "duplicate")}
                              onReplace={() => beginPositionCopy(w.watch_id, "replace")}
                            />
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                ))}
              </TableBody>
            </Table>
          )}

          {/* Pagination Controls */}
          {!watchesLoading && filteredWatches.length > itemsPerPage && (
            <div className="watches-pagination">
              <Mono size=".65rem" color={T.dim}>
                Page {currentPage + 1} of {totalPages}
              </Mono>

              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                  disabled={currentPage === 0}
                >
                  Previous
                </Button>

                {Array.from({ length: totalPages }, (_, i) => (
                  <button
                    key={i}
                    type="button"
                    className="watches-page-btn"
                    aria-current={currentPage === i ? "page" : undefined}
                    onClick={() => setCurrentPage(i)}
                  >
                    {i + 1}
                  </button>
                ))}

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setCurrentPage(Math.min(totalPages - 1, currentPage + 1))
                  }
                  disabled={currentPage === totalPages - 1}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Edit Dialog */}
      <Dialog
        open={isEditOpen}
        onOpenChange={(open) => {
          setIsEditOpen(open);
          if (!open) setUpdatePreview(null);
        }}
      >
        <DialogContent
          className="bg-background border border-border text-foreground w-[92%] md:w-full max-w-2xl"
          style={{
            display: "flex",
            flexDirection: "column",
            maxHeight: "87dvh",
            height: "auto",
            overflow: "hidden",
            padding: 0,
          }}
        >
          <DialogHeader>
            <DialogTitle className="font-syne px-6 pt-4 text-xl font-bold tracking-tight text-foreground">
              Edit Watch
            </DialogTitle>
          </DialogHeader>

          <div
            style={{
              overflowY: "auto",
              flex: 1,
              padding: "0 24px 24px 24px",
              overscrollBehavior: "contain",
            }}
          >
            <div className="space-y-6">
              <fieldset disabled={Boolean(updatePreview)} className="contents">
              <div className="bg-secondary/20 p-4 rounded-lg border border-border flex flex-col sm:flex-row gap-4 sm:gap-6">
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground/80 mb-1">
                    Symbol
                  </div>
                  <div className="text-sm font-bold font-mono text-foreground">
                    {editingWatch?.ticker}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground/80 mb-1">
                    Intent
                  </div>
                  <div className="text-sm font-bold font-mono text-foreground">
                    {editingWatch?.position ? "Position P&L" : editingWatch?.intent?.replace(/_/g, " ")}
                  </div>
                </div>
                <div className="flex-1 overflow-hidden">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground/80 mb-1">
                    Contract
                  </div>
                  <div className="text-sm font-bold font-mono text-foreground truncate">
                    {getWatchContractDisplay(editingWatch)}
                  </div>
                </div>
              </div>

              {editingWatch?.position && (
                <div className="space-y-3 rounded-lg border border-border bg-secondary/20 p-4">
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground/80 mb-1">Position alert</div>
                    <Mono size=".58rem" color={T.dim}>P&amp;L is calculated from the normalized position basis using bid/ask liquidation values. Structure and entry basis are immutable.</Mono>
                  </div>
                  {editingWatch.evaluation_spec?.subject === "POSITION_EXIT" ? (
                    <p className="text-sm text-muted-foreground">
                      This Watch uses versioned exit triggers. This edit changes its schedule and delivery only; its triggers remain unchanged.
                    </p>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <Label htmlFor="edit-position-profit-target" className="mono-label">Profit target (USD)</Label>
                        <Input
                          id="edit-position-profit-target"
                          type="number"
                          inputMode="decimal"
                          min="0.01"
                          step="0.01"
                          className="lh-input"
                          value={positionProfitTarget ?? ""}
                          onChange={(event) => {
                            const value = event.target.value === "" ? null : Number(event.target.value);
                            setPositionProfitTarget(value);
                            setConditions((current) => withPositionPnlCondition(current, "gte", value));
                            setConditionLogic("OR");
                          }}
                        />
                      </div>
                      <div>
                        <Label htmlFor="edit-position-loss-limit" className="mono-label">Loss limit (USD)</Label>
                        <Input
                          id="edit-position-loss-limit"
                          type="number"
                          inputMode="decimal"
                          min="0.01"
                          step="0.01"
                          className="lh-input"
                          value={positionLossLimit ?? ""}
                          onChange={(event) => {
                            const value = event.target.value === "" ? null : Number(event.target.value);
                            setPositionLossLimit(value);
                            setConditions((current) => withPositionPnlCondition(current, "lte", value));
                            setConditionLogic("OR");
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {intent === "iv_rank_threshold" && (
                <div
                  style={{
                    background: T.card2,
                    padding: 18,
                    borderRadius: 6,
                    border: `1px solid ${T.borderSoft}`,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: 12,
                    }}
                  >
                    <Mono size=".7rem">IV Rank Threshold</Mono>
                    <Mono size=".65rem" color={T.primary}>
                      {ivThreshold}th percentile
                    </Mono>
                  </div>
                  <Input
                    type="range"
                    min={0}
                    max={100}
                    step={5}
                    value={ivThreshold}
                    onChange={(e) => setIvThreshold(Number(e.target.value))}
                    style={{ width: "100%", accentColor: T.primary }}
                  />
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginTop: 4,
                    }}
                  >
                    <Mono size=".45rem" color={T.dim}>
                      0 (low IV)
                    </Mono>
                    <Mono size=".45rem" color={T.dim}>
                      100 (high IV)
                    </Mono>
                  </div>
                </div>
              )}

              {intent === "price_target" && (
                <div
                  style={{
                    background: T.card2,
                    padding: 18,
                    borderRadius: 6,
                    border: `1px solid ${T.borderSoft}`,
                  }}
                >
                  <Mono
                    size=".7rem"
                    style={{ marginBottom: 12, display: "block" }}
                  >
                    Price Target
                  </Mono>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 12,
                    }}
                  >
                    <div>
                      <div className="mono-label">Target Price ($)</div>
                      <Input
                        type="number"
                        className="lh-input"
                        value={targetPrice ?? ""}
                        onChange={(e) =>
                          setTargetPrice(
                            e.target.value ? parseFloat(e.target.value) : null,
                          )
                        }
                        placeholder="0.00"
                        min={0.01}
                        step={0.5}
                      />
                    </div>
                    <div>
                      <div className="mono-label">Direction</div>
                      <Select
                        value={targetDirection}
                        onValueChange={(val) =>
                          setTargetDirection(val as "above" | "below")
                        }
                      >
                        <SelectTrigger className="lh-input w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="above">
                            Above (rises to)
                          </SelectItem>
                          <SelectItem value="below">
                            Below (drops to)
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              )}

              {intent === "volatility_spike" && (
                <div
                  style={{
                    background: T.card2,
                    padding: 18,
                    borderRadius: 6,
                    border: `1px solid ${T.borderSoft}`,
                  }}
                >
                  <div className="mono-label">
                    Volatility sensitivity{" "}
                    <InfoTip text="How easily the watch treats a move as a volatility spike." />
                  </div>
                  <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                    {(
                      [
                        {
                          label: "Low",
                          value: "low",
                          desc: "Only bigger spikes",
                        },
                        {
                          label: "Balanced",
                          value: "medium",
                          desc: "Recommended",
                        },
                        {
                          label: "High",
                          value: "high",
                          desc: "Catch smaller jumps",
                        },
                      ] as const
                    ).map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setVolatilitySensitivity(opt.value)}
                        style={{
                          flex: 1,
                          padding: "10px 8px",
                          borderRadius: 6,
                          border:
                            volatilitySensitivity === opt.value
                              ? `1px solid ${T.primary}`
                              : `1px solid ${T.borderSoft}`,
                          background:
                            volatilitySensitivity === opt.value
                              ? "rgba(0,184,219,0.08)"
                              : T.card2,
                          cursor: "pointer",
                          transition: "all 0.15s",
                          textAlign: "center",
                        }}
                      >
                        <div
                          style={{
                            fontFamily: T.mono,
                            fontSize: ".78rem",
                            fontWeight: 600,
                            color:
                              volatilitySensitivity === opt.value
                                ? T.primary
                                : T.text,
                          }}
                        >
                          {opt.label}
                        </div>
                        <div
                          style={{
                            fontFamily: T.mono,
                            fontSize: ".6rem",
                            color: T.dim,
                            marginTop: 2,
                          }}
                        >
                          {opt.desc}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <div className="mono-label">
                  Monitoring window{" "}
                  <InfoTip text="How long to keep this watch active. If you selected an option contract, the watch still stops at contract expiry even if your window is longer." />
                </div>
                <Mono
                  size=".52rem"
                  color={T.dim}
                  style={{ display: "block", marginTop: 4 }}
                >
                  Choose how long to keep this setup live.
                </Mono>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(168px, 1fr))",
                    gap: 8,
                    marginTop: 10,
                  }}
                >
                  {monitoringWindowOptions.map((opt) => (
                    <button
                      key={opt.key}
                      type="button"
                      disabled={isMonitoringWindowDisabled(opt.days)}
                      onClick={() => {
                        if (!isMonitoringWindowDisabled(opt.days)) {
                          setDuration(opt.days);
                        }
                      }}
                      style={{
                        flex: 1,
                        minWidth: 0,
                        padding: "10px 12px",
                        borderRadius: 6,
                        border: isMonitoringWindowActive(opt.days)
                          ? `1px solid ${T.primary}`
                          : `1px solid ${T.borderSoft}`,
                        background: isMonitoringWindowActive(opt.days)
                          ? "rgba(0,184,219,0.08)"
                          : T.card2,
                        cursor: isMonitoringWindowDisabled(opt.days)
                          ? "not-allowed"
                          : "pointer",
                        opacity: isMonitoringWindowDisabled(opt.days) ? 0.45 : 1,
                        transition: "all 0.15s",
                        textAlign: "center",
                      }}
                    >
                      <div
                        style={{
                          fontFamily: T.mono,
                          fontSize: "0.66rem",
                          color: isMonitoringWindowActive(opt.days)
                            ? T.primary
                            : T.text,
                          lineHeight: 1.3,
                        }}
                      >
                        {opt.label}
                      </div>
                      <div
                        style={{
                          fontFamily: T.mono,
                          fontSize: "0.54rem",
                          color: T.dim,
                          marginTop: 4,
                          lineHeight: 1.35,
                        }}
                      >
                        {opt.helper}
                      </div>
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => {
                      if (!lockToExpiryWindow && !isCustomWindowSelected) {
                        setDuration(suggestedCustomWindowDays);
                      }
                    }}
                    disabled={lockToExpiryWindow}
                    style={{
                      minWidth: 0,
                      padding: "10px 12px",
                      borderRadius: 6,
                      border: isCustomWindowSelected
                        ? `1px solid ${T.primary}`
                        : `1px solid ${T.borderSoft}`,
                      background: isCustomWindowSelected
                        ? "rgba(0,184,219,0.08)"
                        : T.card2,
                      cursor: lockToExpiryWindow ? "not-allowed" : "pointer",
                      opacity: lockToExpiryWindow ? 0.45 : 1,
                      transition: "all 0.15s",
                      textAlign: "center",
                    }}
                  >
                    <div
                      style={{
                        fontFamily: T.mono,
                        fontSize: "0.66rem",
                        color: isCustomWindowSelected ? T.primary : T.text,
                        lineHeight: 1.3,
                      }}
                    >
                      Custom window
                    </div>
                    <div
                      style={{
                        fontFamily: T.mono,
                        fontSize: "0.54rem",
                        color: T.dim,
                        marginTop: 4,
                        lineHeight: 1.35,
                      }}
                    >
                      Set an exact number of days
                    </div>
                  </button>
                </div>
                {isCustomWindowSelected && (
                  <div
                    style={{
                      marginTop: 10,
                      padding: 12,
                      borderRadius: 8,
                      border: `1px solid ${T.borderSoft}`,
                      background: T.card2,
                      display: "grid",
                      gap: 10,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 12,
                        flexWrap: "wrap",
                      }}
                    >
                      <Mono size=".58rem" color={T.text}>
                        Custom monitoring window
                      </Mono>
                      <Mono size=".52rem" color={T.dim}>
                        {monitoringWindowSummary}
                      </Mono>
                    </div>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "minmax(0, 180px) minmax(0, 1fr)",
                        gap: 12,
                        alignItems: "end",
                      }}
                    >
                      <div>
                        <div className="mono-label" style={{ marginBottom: 6 }}>
                          Window (days){" "}
                          <InfoTip text="Set the exact number of calendar days to keep this watch active. Contract-based watches still stop at expiry." />
                        </div>
                        <Input
                          type="number"
                          className="lh-input"
                          value={effectiveDuration}
                          onChange={(e) =>
                            setDuration(parseInt(e.target.value) || 0)
                          }
                          min={1}
                          max={30}
                          disabled={lockToExpiryWindow}
                        />
                      </div>
                      <Mono
                        size=".52rem"
                        color={T.dim}
                        style={{ lineHeight: 1.45 }}
                      >
                        {contractExpiryInfo
                          ? "If your custom window extends past expiry, the watch ends at expiry instead."
                          : "Use a custom window when none of the presets matches your thesis horizon."}
                      </Mono>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <div className="mono-label">
                  After first alert{" "}
                  <InfoTip text="What happens after the watch triggers" />
                </div>
                <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                  {(
                    [
                      {
                        label: "Done",
                        desc: "One alert, then complete",
                        mode: "one_and_done",
                      },
                      {
                        label: "Keep watching",
                        desc: "Continue for repeat signals",
                        mode: "keep_watching",
                      },
                    ] as const
                  ).map((opt) => (
                    <button
                      key={opt.mode}
                      type="button"
                      onClick={() => applyAlertModeDefaults(opt.mode, intent)}
                      style={{
                        flex: 1,
                        padding: "10px 12px",
                        borderRadius: 6,
                        border:
                          alertMode === opt.mode
                            ? `1px solid ${T.primary}`
                            : `1px solid ${T.borderSoft}`,
                        background:
                          alertMode === opt.mode
                            ? "rgba(0,184,219,0.08)"
                            : T.card2,
                        cursor: "pointer",
                        transition: "all 0.15s",
                        textAlign: "center",
                      }}
                    >
                      <div
                        style={{
                          fontFamily: T.mono,
                          fontSize: ".78rem",
                          fontWeight: 600,
                          color: alertMode === opt.mode ? T.primary : T.text,
                        }}
                      >
                        {opt.label}
                      </div>
                      <div
                        style={{
                          fontFamily: T.mono,
                          fontSize: ".6rem",
                          color: T.dim,
                          marginTop: 2,
                        }}
                      >
                        {opt.desc}
                      </div>
                    </button>
                  ))}
                </div>
                <Mono
                  size=".52rem"
                  color={T.dim}
                  style={{
                    display: "block",
                    marginTop: 8,
                    lineHeight: 1.45,
                  }}
                >
                  {alertMode === "one_and_done"
                    ? "This watch sends one alert and then stops."
                    : "This watch keeps monitoring after the first alert. You can fine-tune repeat timing in Advanced settings."}
                </Mono>
              </div>

              {(intent === "good_time_to_buy" ||
                intent === "good_time_to_sell") && (
                <div>
                  <div className="mono-label">
                    Sensitivity{" "}
                    <InfoTip text="How selective the model is before triggering an alert" />
                  </div>
                  <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                    {(
                      [
                        { label: "Low", value: 0.5, desc: "More alerts" },
                        {
                          label: "Balanced",
                          value: 0.7,
                          desc: "Recommended",
                        },
                        { label: "High", value: 0.9, desc: "Fewer, sharper" },
                      ] as const
                    ).map((opt) => (
                      <button
                        key={opt.label}
                        type="button"
                        onClick={() => setConfidenceThreshold(opt.value)}
                        style={{
                          flex: 1,
                          padding: "10px 8px",
                          borderRadius: 6,
                          border:
                            confidenceThreshold === opt.value
                              ? `1px solid ${T.primary}`
                              : `1px solid ${T.borderSoft}`,
                          background:
                            confidenceThreshold === opt.value
                              ? "rgba(0,184,219,0.08)"
                              : T.card2,
                          cursor: "pointer",
                          transition: "all 0.15s",
                          textAlign: "center",
                        }}
                      >
                        <div
                          style={{
                            fontFamily: T.mono,
                            fontSize: ".78rem",
                            fontWeight: 600,
                            color:
                              confidenceThreshold === opt.value
                                ? T.primary
                                : T.text,
                          }}
                        >
                          {opt.label}
                        </div>
                        <div
                          style={{
                            fontFamily: T.mono,
                            fontSize: ".6rem",
                            color: T.dim,
                            marginTop: 2,
                          }}
                        >
                          {opt.desc}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "8px 0",
                }}
              >
                <div>
                  <Mono size=".58rem" color={T.text}>
                    Market hours only
                  </Mono>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-label="Market hours only"
                  aria-checked={marketHoursOnly}
                  onClick={() => setMarketHoursOnly(!marketHoursOnly)}
                  style={{
                    width: 36,
                    height: 20,
                    borderRadius: 10,
                    border: `1px solid ${T.borderSoft}`,
                    cursor: "pointer",
                    background: marketHoursOnly ? `${T.primary}20` : T.card,
                    position: "relative",
                    transition: "background 0.2s",
                  }}
                >
                  <div
                    style={{
                      width: 14,
                      height: 14,
                      borderRadius: "50%",
                      background: marketHoursOnly ? T.primary : T.dim,
                      position: "absolute",
                      top: 2,
                      left: marketHoursOnly ? 19 : 3,
                      transition: "left 0.2s",
                    }}
                  />
                </button>
              </div>

              <WatchSummary
                ticker={ticker}
                intent={intent}
                contractExpiry={contractExpiry}
                contractStrike={contractStrike}
                contractType={contractType}
                policy={{
                  max_duration_days: effectiveDuration,
                  evaluation_interval_minutes: interval,
                  max_alerts_per_watch:
                    alertMode === "one_and_done" ? 1 : maxAlerts,
                  cooldown_minutes: cooldown,
                  market_hours_only: marketHoursOnly,
                }}
                intentParams={
                  intent === "iv_rank_threshold"
                    ? { threshold: ivThreshold }
                    : intent === "price_target"
                      ? {
                          target_price: targetPrice,
                          direction: targetDirection,
                        }
                      : intent === "volatility_spike"
                        ? { sensitivity: volatilitySensitivity }
                        : { confidence_threshold: confidenceThreshold }
                }
                alertMode={alertMode}
              />

              <div style={{ marginTop: 8 }}>
                <button
                  type="button"
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    fontFamily: T.mono,
                    fontSize: "0.65rem",
                    color: T.dim,
                    padding: 0,
                    transition: "color 0.15s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = T.primary;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = T.dim;
                  }}
                >
                  {showAdvanced
                    ? "Hide advanced settings"
                    : "Advanced settings"}
                </button>
                {showAdvanced && (
                  <div
                    style={{
                      marginTop: 12,
                      padding: 14,
                      borderRadius: 10,
                      border: `1px solid ${T.borderSoft}`,
                      background: T.card2,
                      display: "grid",
                      gap: 12,
                    }}
                  >
                    <Mono
                      size=".54rem"
                      color={T.dim}
                      style={{ lineHeight: 1.45 }}
                    >
                      Fine-tune how often Oracle checks this watch. Repeat
                      timing only appears when the watch keeps running after the
                      first alert.
                    </Mono>
                    <div
                      style={{
                        display: "grid",
                        gap: 14,
                        alignItems: "end",
                      }}
                      className={
                        alertMode === "keep_watching"
                          ? "grid-cols-1 sm:grid-cols-2"
                          : "grid-cols-1"
                      }
                    >
                      <div>
                        <div
                          className="mono-label"
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                            whiteSpace: "nowrap",
                          }}
                        >
                          Evaluation interval (min){" "}
                          <InfoTip text="How often Oracle reevaluates the setup during the monitoring window." />
                        </div>
                        <Input
                          type="number"
                          className="lh-input"
                          value={interval}
                          onChange={(e) =>
                            setInterval(parseInt(e.target.value) || 0)
                          }
                          min={5}
                          max={1440}
                        />
                      </div>
                      {alertMode === "keep_watching" && (
                        <div>
                          <div
                            className="mono-label"
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 4,
                              whiteSpace: "nowrap",
                            }}
                          >
                            Min gap between alerts (min){" "}
                            <InfoTip text="Minimum time Oracle waits before sending another alert on the same watch." />
                          </div>
                          <Input
                            type="number"
                            className="lh-input"
                            value={cooldown}
                            onChange={(e) =>
                              setCooldown(parseInt(e.target.value) || 0)
                            }
                            min={0}
                            max={10080}
                          />
                        </div>
                      )}
                    </div>
                    {alertMode === "one_and_done" && (
                      <Mono
                        size=".5rem"
                        color={T.dim}
                        style={{ lineHeight: 1.4 }}
                      >
                        Repeat settings are off because this watch stops after
                        its first alert.
                      </Mono>
                    )}
                  </div>
                )}
              </div>

              {!editingWatch?.position && recipeConditions.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground/80">
                    Recipe triggers
                  </Label>
                  <div className="space-y-3">
                    {recipeGroupViews.map((group) => (
                      <div key={group.id} className="space-y-2">
                        <div className="space-y-0.5">
                          <Mono size=".58rem" color={T.text}>
                            {group.label}
                          </Mono>
                          <Mono size=".5rem" color={T.dim}>
                            {group.description}
                            {group.id === "core" &&
                            group.selection_mode === "at_least_one"
                              ? " At least one enabled setup trigger must pass."
                              : ""}
                          </Mono>
                        </div>
                        {group.conditions.map((condition) => (
                          <button
                            key={condition.id}
                            type="button"
                            onClick={() => {
                              if (condition.required) return;
                              toggleRecipeCondition(condition.id);
                            }}
                            className="w-full rounded-lg border border-border px-3 py-2 text-left"
                            disabled={condition.required}
                          >
                            <div className="flex items-center justify-between gap-3">
                              <Mono size=".65rem" color={T.text}>
                                {condition.label || condition.id}
                              </Mono>
                              <Mono
                                size=".55rem"
                                color={condition.enabled ? T.primary : T.dim}
                              >
                                {condition.required
                                  ? "Required"
                                  : condition.enabled
                                    ? "On"
                                    : "Off"}
                              </Mono>
                            </div>
                            {condition.description && (
                              <Mono size=".52rem" color={T.dim}>
                                {condition.description}
                              </Mono>
                            )}
                          </button>
                        ))}
                      </div>
                    ))}
                    {!hasValidRecipeSelection && (
                      <Mono size=".55rem" color={T.red}>
                        At least one trade-setup trigger must remain enabled.
                      </Mono>
                    )}
                  </div>
                </div>
              )}
              </fieldset>

              {updatePreview && (
                <div
                  className="rounded-lg border border-primary/40 bg-primary/5 p-4"
                  role="status"
                  aria-label="Watch update review"
                >
                  <div className="text-sm font-bold text-foreground">Review changes</div>
                  <div className="mt-1 text-xs leading-relaxed text-muted-foreground">
                    Confirm to apply these settings. The Watch will keep its current contract and history.
                  </div>
                  <ul className="mt-3 space-y-1 text-xs text-foreground">
                    {(updatePreview.changedFields.length > 0
                      ? updatePreview.changedFields
                      : ["Watch settings"]
                    ).map((field) => (
                      <li key={field}>• {field.replace(/[._]/g, " ")}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <Button
                  className="flex-1 h-10"
                  onClick={handleUpdateWatch}
                  disabled={isSubmitting || (!editingWatch?.position && !hasValidRecipeSelection)}
                >
                  {isSubmitting
                    ? updatePreview
                      ? "Confirming..."
                      : "Preparing..."
                    : updatePreview
                      ? "Confirm Changes"
                      : "Review Changes"}
                </Button>
                <Button
                  variant="outline"
                  className="w-24 h-10"
                  onClick={() => {
                    if (updatePreview) setUpdatePreview(null);
                    else setIsEditOpen(false);
                  }}
                  disabled={isSubmitting}
                >
                  {updatePreview ? "Back" : "Cancel"}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Action Confirmation Dialog */}
      <Dialog open={isConfirmOpen} onOpenChange={setIsConfirmOpen}>
        <DialogContent className="bg-background border border-border text-foreground max-w-[360px] p-6 shadow-2xl rounded-2xl">
          <DialogHeader className="pb-2">
            <DialogTitle className="font-syne text-xl font-bold tracking-tight text-foreground">
              {pendingAction?.type === "delete"
                ? "Delete Watch"
                : "Cancel Watch?"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            <Mono
              size=".7rem"
              color={T.dim}
              className="leading-relaxed opacity-80"
            >
              {pendingAction?.type === "delete"
                ? "This will permanently remove the watch and its history. This action cannot be undone."
                : "This will stop all future evaluations. You cannot resume a cancelled watch."}
            </Mono>
            <div className="flex justify-end gap-6 pt-2">
              <button
                className={`flex items-center gap-2 cursor-pointer ${pendingAction?.type === "delete" ? "text-red-500" : "text-orange-400"}`}
                onClick={() => {
                  if (pendingAction) {
                    handleAction(pendingAction.id, pendingAction.type, true);
                    setIsConfirmOpen(false);
                    setPendingAction(null);
                  }
                }}
              >
                {pendingAction?.type === "delete" ? (
                  <Trash2 size={13} />
                ) : (
                  <Ban size={13} />
                )}{" "}
                Confirm
              </button>
              <button
                className="btn-ghost cursor-pointer"
                style={{
                  width: "fit-content",
                  padding: 12,
                  borderRadius: 12,
                  fontSize: ".8rem",
                  cursor: "pointer",
                }}
                onClick={() => setIsConfirmOpen(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default WatchesTab;
